/**
 * Shipping Fee Cart Calculator — Hidden Product Approach
 *
 * Manages a hidden "Shipping Fee" product in the cart to make the per-date
 * shipping fee an actual charge that flows through to Shopify checkout.
 *
 * Performance optimizations:
 * - Uses cart data from 'cart-loading-finish' event directly (no redundant /cart.js fetch)
 * - Calculates expected totals after fee sync instead of re-fetching
 * - Minimal debounce (50ms) for responsive updates
 *
 * Race condition handling:
 * - A _syncing flag prevents recursive cart updates when our own add/change
 *   calls trigger the cart-loading-finish event
 *
 * Badge count handling:
 * - updateCartBadge() is the single source of truth (uses fresh cart data)
 * - cart:count events are NOT corrected (may already be corrected by cart.ajax.liquid)
 * - cart.ajax.liquid already excludes fee items from item_count for cart page updates
 *
 * Theme event integration:
 * - Uses jQuery 'cart-loading-finish' on window (cart data included in event)
 * - Listens to CustomEvent 'cart:count' on document (triggers fetch for fresh data)
 * - Uses MutationObserver on cart items container as fallback
 */

(function() {
  'use strict';

  var PROPERTY_NAMES = ['Delivery Date', 'Harvest Date'];
  var DEBUG = false; // Set to true to enable console logging

  // Flag to prevent recursive fee sync when our own API calls trigger cart events
  var _syncing = false;
  // Debounce timer for rapid cart changes
  var _debounceTimer = null;
  var DEBOUNCE_MS = 50;
  // Last known fee item count (for synchronous badge correction)
  var _lastFeeQty = 0;
  // Cached config (parsed once, reused)
  var _config = null;
  // Track previously set cart attribute keys so we can clear stale ones
  var _lastAttributeKeys = [];

  function log() {
    if (DEBUG) {
      console.log.apply(console, ['[shipping-fee]'].concat(
        Array.prototype.slice.call(arguments)
      ));
    }
  }

  /**
   * Get or parse the config from the DOM. Cached after first parse.
   */
  function getConfig() {
    if (_config) return _config;
    var configEl = document.querySelector('[data-shipping-fee-config]');
    if (!configEl) return null;
    try {
      _config = JSON.parse(configEl.textContent);
      log('Config loaded:', _config);
    } catch (e) {
      console.error('[shipping-fee] Failed to parse config', e);
      return null;
    }
    return _config;
  }

  /**
   * Parse a date string into YYYY-MM-DD format for comparison.
   */
  function parseDate(str) {
    if (!str || typeof str !== 'string') return null;
    str = str.trim();
    var isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return isoMatch[1] + '-' + isoMatch[2] + '-' + isoMatch[3];
    }
    var dateObj = new Date(str);
    if (!isNaN(dateObj.getTime())) {
      var y = dateObj.getFullYear();
      var m = ('0' + (dateObj.getMonth() + 1)).slice(-2);
      var d = ('0' + dateObj.getDate()).slice(-2);
      return y + '-' + m + '-' + d;
    }
    return null;
  }

  /**
   * Decode HTML entities in a string (e.g., &lt; → <, &amp; → &).
   * Shopify's /cart.js returns HTML-encoded titles, but line item
   * property values should contain plain text.
   */
  function decodeHtml(str) {
    if (!str || typeof str !== 'string') return str;
    var txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  /**
   * Extract unique delivery dates from cart items.
   * Also maps products to each date for checkout display.
   * Sorted chronologically (earliest first).
   */
  function extractUniqueDates(cartItems) {
    var dates = {};
    var productsByDate = {};
    for (var i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      if (item.properties && item.properties._shipping_fee) continue;
      if (!item.properties) continue;
      for (var j = 0; j < PROPERTY_NAMES.length; j++) {
        var dateStr = item.properties[PROPERTY_NAMES[j]];
        if (dateStr) {
          var parsed = parseDate(dateStr);
          if (parsed) {
            dates[parsed] = dateStr;
            if (!productsByDate[parsed]) {
              productsByDate[parsed] = [];
            }
            // Avoid duplicate product names per date
            // Decode HTML entities so checkout shows "<" instead of "&lt;"
            var decodedTitle = decodeHtml(item.title);
            if (productsByDate[parsed].indexOf(decodedTitle) === -1) {
              productsByDate[parsed].push(decodedTitle);
            }
          }
        }
      }
    }
    var result = [];
    for (var key in dates) {
      if (dates.hasOwnProperty(key)) {
        result.push({
          key: key,
          display: dates[key],
          products: productsByDate[key] || []
        });
      }
    }
    result.sort(function(a, b) {
      return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });
    return result;
  }

  /**
   * Calculate shipping fee: baseRate + uniqueDates × feePerDate
   */
  function calculateFee(uniqueDates, config) {
    var dateCount = uniqueDates.length;
    if (dateCount === 0) {
      return { amount: 0, dateCount: 0 };
    }
    var amount = config.baseRate + (dateCount * config.feePerDate);
    return { amount: amount, dateCount: dateCount };
  }

  function formatMoney(cents, symbol) {
    return symbol + (cents / 100).toFixed(2);
  }

  function findFeeItems(cartItems) {
    return cartItems.filter(function(item) {
      return item.properties && item.properties._shipping_fee === 'true';
    });
  }

  /**
   * Format a date's value based on the checkout display setting.
   * Modes: 'date-products', 'date-count', 'date-only', 'off'
   */
  function formatDateValue(date, mode) {
    switch (mode) {
      case 'date-products':
        return date.products.length > 0 ? date.products.join(', ') : '';
      case 'date-count':
        var count = date.products.length;
        return count > 0 ? count + ' product' + (count > 1 ? 's' : '') : '';
      case 'date-only':
        return '';
      default:
        return '';
    }
  }

  /**
   * Sync cart attributes so delivery dates and their products show at checkout.
   * Cart attributes appear in the checkout sidebar under "Additional details".
   * Respects the checkoutDisplay setting ('date-products', 'date-count', 'date-only', 'off').
   *
   * Old attributes are cleared by setting them to empty string.
   * Only makes the API call when attributes actually change.
   */
  function syncCartAttributes(uniqueDates, config) {
    var displayMode = config.checkoutDisplay || 'off';

    // Always clear previous attributes, even if setting changed
    var attributes = {};

    for (var i = 0; i < _lastAttributeKeys.length; i++) {
      attributes[_lastAttributeKeys[i]] = '';
    }

    var newKeys = [];

    // Only set new attributes if the setting is not 'off'
    if (displayMode !== 'off') {
      for (var i = 0; i < uniqueDates.length; i++) {
        var date = uniqueDates[i];
        var key = 'Delivery (' + date.display + ')';
        var value = formatDateValue(date, displayMode);
        attributes[key] = value;
        newKeys.push(key);
      }

      // Also set a summary attribute
      if (uniqueDates.length > 0) {
        var summaryKey = 'Shipping';
        var dateCount = uniqueDates.length;
        var summaryValue = dateCount + ' delivery date' + (dateCount > 1 ? 's' : '') + ' × ' + formatMoney(config.feePerDate, config.currencySymbol);
        attributes[summaryKey] = summaryValue;
        newKeys.push(summaryKey);
      }
    }

    // Check if attributes actually changed (avoid unnecessary API calls)
    var changed = false;
    if (newKeys.length !== _lastAttributeKeys.length) {
      changed = true;
    } else {
      for (var i = 0; i < newKeys.length; i++) {
        if (newKeys[i] !== _lastAttributeKeys[i]) {
          changed = true;
          break;
        }
      }
    }

    _lastAttributeKeys = newKeys;

    if (changed) {
      log('Syncing cart attributes:', attributes);
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attributes })
      }).catch(function(err) {
        console.error('[shipping-fee] Cart attribute sync failed', err);
      });
    }
  }

  /**
   * Process cart data: calculate fees, sync fee product, render.
   * Accepts cart data directly (no fetch needed).
   */
  function processCart(cart) {
    var config = getConfig();
    if (!config) return;
    if (_syncing) return;

    var uniqueDates = extractUniqueDates(cart.items);
    var feeResult = calculateFee(uniqueDates, config);
    var feeItems = findFeeItems(cart.items);
    var feeTotalCents = feeItems.reduce(function(sum, item) {
      return sum + item.line_price;
    }, 0);
    var subtotalCents = cart.total_price - feeTotalCents;

    // Render immediately with current data (optimistic)
    renderSummary(uniqueDates, feeResult, config, subtotalCents, feeTotalCents);
    updateCartBadge(cart);

    // Sync cart attributes for checkout display (delivery dates + products)
    syncCartAttributes(uniqueDates, config);

    // Always sync fee product when there are dates or existing fee items
    // syncFeeProduct checks if quantity AND properties are correct
    var variantId = config.variantId;
    if (!variantId || variantId === 0) return;

    if (feeResult.dateCount > 0 || feeItems.length > 0) {
      syncFeeProduct(cart, uniqueDates, config);
    }
  }

  /**
   * Sync the fee product in the cart (background operation).
   * After syncing, re-renders with fresh cart data.
   */
  function syncFeeProduct(cart, uniqueDates, config) {
    var variantId = config.variantId;
    var feeItems = findFeeItems(cart.items);
    var feeResult = calculateFee(uniqueDates, config);
    var requiredQty = feeResult.dateCount;

    // Remove all fee items if no dates
    if (requiredQty <= 0) {
      if (feeItems.length === 0) return;
      _syncing = true;
      Promise.all(feeItems.map(function(item) {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: item.key, quantity: 0 })
        }).then(function(r) { return r.json(); });
      })).then(function() {
        setTimeout(function() { _syncing = false; }, 300);
        return fetchAndProcessCart();
      });
      return;
    }

    var currentFeeQty = feeItems.reduce(function(sum, item) {
      return sum + item.quantity;
    }, 0);

    var dateDisplayList = uniqueDates.map(function(d) { return d.display; });
    var dateBreakdown = requiredQty + ' delivery date' + (requiredQty > 1 ? 's' : '') + ' × ' + formatMoney(config.feePerDate, config.currencySymbol);
    var uniqueDatesStr = dateDisplayList.join(', ');

    // Build properties: underscore-prefixed are hidden from checkout
    // Visible properties (no underscore) show at checkout under the Shipping Fee line item
    var feeProperties = {
      '_shipping_fee': 'true',
      '_date_breakdown': dateBreakdown,
      '_unique_dates': uniqueDatesStr
    };

    // Add visible properties for each delivery date (checkout display)
    var displayMode = config.checkoutDisplay || 'off';
    if (displayMode !== 'off') {
      for (var i = 0; i < uniqueDates.length; i++) {
        var date = uniqueDates[i];
        var value = formatDateValue(date, displayMode);
        if (displayMode === 'date-only') {
          // For date-only mode, use a single property key with all dates
          // (individual date keys with empty values would look odd)
          // We'll add them below after the loop
        } else {
          feeProperties[date.display] = value;
        }
      }
      // For date-only mode, add a single property listing all dates
      if (displayMode === 'date-only') {
        feeProperties['Delivery dates'] = dateDisplayList.join(', ');
      }
    }

    // Check if fee item already has correct quantity AND matching properties
    if (feeItems.length > 0 && currentFeeQty === requiredQty) {
      var currentProps = feeItems[0].properties || {};
      var propsMatch = true;

      // Check all expected properties exist with correct values
      for (var key in feeProperties) {
        if (feeProperties.hasOwnProperty(key)) {
          if (currentProps[key] !== feeProperties[key]) {
            propsMatch = false;
            break;
          }
        }
      }

      // Check no stale properties remain (e.g., old date properties from a removed date)
      if (propsMatch) {
        for (var key in currentProps) {
          if (currentProps.hasOwnProperty(key) && !feeProperties.hasOwnProperty(key)) {
            propsMatch = false;
            break;
          }
        }
      }

      if (propsMatch) {
        // Everything is in order, no sync needed
        return;
      }

      // Quantity correct but properties changed — just update properties
      _syncing = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: feeItems[0].key,
          quantity: requiredQty,
          properties: feeProperties
        })
      }).then(function(r) { return r.json(); }).then(function() {
        setTimeout(function() { _syncing = false; }, 300);
        fetchAndProcessCart();
      });
      return;
    }

    // Remove existing, then add fresh
    _syncing = true;
    var removePromise = feeItems.length > 0
      ? Promise.all(feeItems.map(function(item) {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ id: item.key, quantity: 0 })
          }).then(function(r) { return r.json(); });
        }))
      : Promise.resolve([]);

    removePromise.then(function() {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: variantId,
          quantity: requiredQty,
          properties: feeProperties
        })
      });
    }).then(function(r) { return r.json(); }).then(function() {
      setTimeout(function() { _syncing = false; }, 300);
      fetchAndProcessCart();
    }).catch(function(err) {
      console.error('[shipping-fee] Fee sync failed', err);
      _syncing = false;
    });
  }

  /**
   * Fetch cart and process (used after fee sync to get fresh totals).
   */
  function fetchAndProcessCart() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        processCart(cart);
      });
  }

  /**
   * Render the shipping fee summary in the cart.
   */
  function renderSummary(uniqueDates, feeResult, config, subtotalCents, feeTotalCents) {
    var summaryEl = document.querySelector('[data-shipping-fee-summary]');
    if (!summaryEl) return;

    var subtotalEl = summaryEl.querySelector('[data-shipping-fee-subtotal]');
    var amountEl = summaryEl.querySelector('[data-shipping-fee-amount]');
    var dateCountEl = summaryEl.querySelector('[data-shipping-fee-date-count]');
    var totalEl = summaryEl.querySelector('[data-shipping-fee-total]');
    var shippingRow = summaryEl.querySelector('.shipping-fee-shipping-row');
    var explanationEl = summaryEl.querySelector('[data-shipping-fee-explanation]');
    var dateListEl = summaryEl.querySelector('[data-shipping-fee-date-list]');

    var totalCents = subtotalCents + feeResult.amount;

    // Show the summary, hide original price span
    summaryEl.style.display = '';
    var originalPrice = document.querySelector('[data-cart-price]');
    if (originalPrice) {
      originalPrice.style.display = 'none';
    }

    // Subtotal
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(subtotalCents, config.currencySymbol);
    }

    // Shipping row
    if (feeResult.dateCount === 0) {
      if (shippingRow) shippingRow.style.display = 'none';
      if (amountEl) amountEl.textContent = formatMoney(0, config.currencySymbol);
      if (dateCountEl) dateCountEl.textContent = '';
      var detailsEl = summaryEl.querySelector('[data-shipping-fee-details]');
      if (detailsEl) detailsEl.removeAttribute('data-open');
      var toggleBtn = summaryEl.querySelector('[data-shipping-fee-toggle]');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    } else {
      if (shippingRow) shippingRow.style.display = '';
      if (amountEl) amountEl.textContent = formatMoney(feeResult.amount, config.currencySymbol);
      if (dateCountEl) {
        var dateWord = feeResult.dateCount === 1 ? 'date' : 'dates';
        dateCountEl.textContent = '(' + feeResult.dateCount + ' ' + dateWord + ')';
      }
      if (explanationEl) {
        explanationEl.textContent = formatMoney(config.feePerDate, config.currencySymbol) + ' per delivery date';
      }
      if (dateListEl) {
        dateListEl.innerHTML = '';
        for (var i = 0; i < uniqueDates.length; i++) {
          var li = document.createElement('li');
          li.textContent = uniqueDates[i].display + ' — ' + formatMoney(config.feePerDate, config.currencySymbol);
          dateListEl.appendChild(li);
        }
      }
    }

    // Total
    if (totalEl) {
      totalEl.textContent = formatMoney(totalCents, config.currencySymbol);
    }
  }

  /**
   * Update the header cart badge count to exclude fee items.
   */
  function updateCartBadge(cart) {
    var feeItems = findFeeItems(cart.items);
    _lastFeeQty = feeItems.reduce(function(sum, item) {
      return sum + item.quantity;
    }, 0);
    setBadgeCount(Math.max(0, cart.item_count - _lastFeeQty));
  }

  function setBadgeCount(count) {
    var cartCountEls = document.querySelectorAll('.site-header__cart-count');
    for (var i = 0; i < cartCountEls.length; i++) {
      cartCountEls[i].textContent = count;
    }
  }

  function interceptCartCount(e) {
    // Don't correct the badge here — cart:count events may carry either:
    // - The raw count (from Shopify.getCart on product page adds) — needs correction
    // - The already-corrected count (from cart.ajax.liquid on cart page updates) — no correction needed
    // We can't reliably tell which, so just schedule a fetch.
    // updateCartBadge() is the single source of truth for badge correction.
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(fetchAndProcessCart, DEBOUNCE_MS);
  }

  function toggleDetails() {
    var detailsEl = document.querySelector('[data-shipping-fee-details]');
    var toggleBtn = document.querySelector('[data-shipping-fee-toggle]');
    if (!detailsEl || !toggleBtn) return;
    if (detailsEl.hasAttribute('data-open')) {
      detailsEl.removeAttribute('data-open');
      toggleBtn.setAttribute('aria-expanded', 'false');
    } else {
      detailsEl.setAttribute('data-open', '');
      toggleBtn.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Initialize: render on load, bind events.
   */
  function init() {
    log('Initializing shipping fee calculator...');

    // Initial render (fetch cart on first load since we don't have event data yet)
    fetchAndProcessCart();

    // Bind dropdown toggle
    var toggleBtn = document.querySelector('[data-shipping-fee-toggle]');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function(e) {
        e.preventDefault();
        toggleDetails();
      });
    }

    // Primary: jQuery 'cart-loading-finish' — cart data IS included in the event
    if (typeof jQuery !== 'undefined') {
      jQuery(window).on('cart-loading-finish', function(e, data) {
        if (_syncing) return;
        clearTimeout(_debounceTimer);
        if (data && data.cart) {
          // Use cart data directly — no fetch needed, instant render
          log('cart-loading-finish: using event cart data directly');
          processCart(data.cart);
        } else {
          // Fallback: fetch if event data is missing
          _debounceTimer = setTimeout(fetchAndProcessCart, DEBOUNCE_MS);
        }
      });
    }

    // Listen for cart:count events (from add-to-cart and cart page updates)
    // Don't correct badge here — count may already be corrected (cart.ajax.liquid)
    // or raw (Shopify.getCart). updateCartBadge() handles correction with fresh data.
    document.addEventListener('cart:count', function(e) {
      interceptCartCount(e);
    });

    // Fallback: MutationObserver on cart items container
    var cartItems = document.querySelector('[data-cart-items]');
    if (cartItems) {
      var observer = new MutationObserver(function() {
        clearTimeout(_debounceTimer);
        _debounceTimer = setTimeout(fetchAndProcessCart, DEBOUNCE_MS);
      });
      observer.observe(cartItems, { childList: true, subtree: true });
    }

    log('Shipping fee calculator initialized');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();